import React, {
  useEffect,
  useCallback,
  useRef,
  useState,
  useMemo,
} from 'react';

import { Builder } from '@builder.io/react';
import * as Sentry from '@sentry/nextjs';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { StyleContext, useBreakpoint } from '@techstyle/react-components';
import { loadSession, useSession } from '@techstyle/redux-core';

import createPage from '../../registry';
import BuilderComponent from '../../src/components/BuilderComponent';
import DefaultHead from '../../src/components/DefaultHead/DefaultHead';
import { Component as ErrorPageLayout } from '../../src/components/ErrorPageLayout';
import { ErrorTemplate } from '../../src/components/ErrorTemplate/ErrorTemplate';
import { Component as MainTemplate } from '../../src/components/MainTemplate';
import PromoTrackingWrapper from '../../src/components/PromoTrackingWrapper';
import { desktop } from '../../src/styles';
import { getBuilderJsonSSR } from '../../src/utils/getBuilderJsonSSR';
import { isBuilderPreview } from '../../src/utils/isBuilderPreview';
import {
  nodeComponents,
  registerBuilderComponents,
  registerBuilderNodesMenu,
} from '../../src/utils/registerBuilderComponents';
import { useBuilderPageContent } from '../../src/utils/useBuilderPageContent';

const StyledMainTemplate = styled(MainTemplate)`
  overflow-x: unset;
  ${({ desktopBackground, mobileBackground }) =>
    (desktopBackground || mobileBackground) &&
    css`
      ${MainTemplate.Wrapper} {
        background-image: url(${mobileBackground || desktopBackground});
        background-repeat: no-repeat;
        background-attachment: fixed;
        background-position: top center;
        background-size: cover;

        ${desktop`
          background-image: url(${desktopBackground});
        `}
      }
    `};
`;

const Container = styled.div`
  .builder-accordion-title-open svg.chevron-icon {
    transform: rotate(180deg);
    transition: 0.3s;
  }
`;

export function BuilderPage({
  page,
  error,
  builderStateData,
  modelName,
  urlPath,
  isPreview,
}) {
  const { isMobile } = useBreakpoint();

  const { pageContent, isLoading } = useBuilderPageContent({
    page,
    modelName,
    urlPath,
  });

  const {
    hideSkinnyBanner,
    desktopBackground,
    mobileBackground,
    hideHeader,
    showCountdownSideWidget,
    title,
    description,
    canonicalUrl,
  } = pageContent?.data || {};
  const [contentData, setContentData] = useState(pageContent?.data || {});
  const isEditing = Builder.isEditing;
  const isLive = !isEditing && !isPreview;

  const handleContentLoaded = useCallback(
    pageContentData => {
      // on preview, we want to update both the model fields (e.g. backgrounds)
      // once the content is loaded, since it's never loaded on the server side
      if (isLive || !pageContentData) {
        return;
      }

      setContentData(pageContentData || {});
    },
    [isLive, setContentData]
  );

  const showCounter = isLive
    ? showCountdownSideWidget
    : contentData.showCountdownSideWidget;

  const oneHour = 60 * 60 * 1000;
  const context = useMemo(() => {
    const builderPreviewTime = Date.now() + oneHour;
    if (isMobile) {
      return {
        shopping: showCounter,
        isBuilderPreview: !isLive,
        builderPreviewTime,
        autoHideNav: true,
        mobileProducts: true,
      };
    }
    return {
      shopping: showCounter,
      isBuilderPreview: !isLive,
      builderPreviewTime,
      autoHideNav: true,
    };
  }, [isMobile, showCounter, isLive, oneHour]);

  if (isLive && !isLoading && (error || !pageContent)) {
    return (
      <ErrorTemplate>
        <ErrorPageLayout statusCode={error ? 500 : 404} />
      </ErrorTemplate>
    );
  }

  return (
    <>
      <DefaultHead
        title={isLive ? title : contentData.title}
        description={isLive ? description : contentData.description}
        canonicalUrl={isLive ? canonicalUrl : contentData.canonicalUrl}
      />
      <PromoTrackingWrapper>
        <StyleContext context={context}>
          <StyledMainTemplate
            hideSkinnyBanner={
              isLive ? hideSkinnyBanner : contentData.hideSkinnyBanner
            }
            desktopBackground={
              isLive ? desktopBackground : contentData.desktopBackground
            }
            mobileBackground={
              isLive ? mobileBackground : contentData.mobileBackground
            }
            hideHeader={isLive ? hideHeader : contentData.hideHeader}
            allowNewPricingSkinnyBanner
          >
            <Container>
              <BuilderComponent
                model={modelName}
                content={pageContent}
                data={builderStateData}
                contentLoaded={handleContentLoaded}
              />
            </Container>
          </StyledMainTemplate>
        </StyleContext>
      </PromoTrackingWrapper>
    </>
  );
}

export function BuilderPageOrNavigate(props) {
  const { page } = props;
  const router = useRouter();
  const [notFound, setNotFound] = useState(false);
  const { isLoggedIn } = useSession();
  const prevIsLoggedIn = useRef(isLoggedIn);

  useEffect(() => {
    if (!page) {
      if (router.asPath.startsWith('/builder/')) {
        setNotFound(true);
        return;
      }

      router.replace(router.asPath).catch(err => {
        // If there's no fallback page with a matching Next.js route,
        // `router.replace` will throw
        // "Invariant: attempted to hard navigate to the same URL...".
        // But in this case it means that the page doesn't exist, hence a
        // 404 error is shown.
        if (err.message?.includes('Invariant')) {
          setNotFound(true);
        } else {
          throw err;
        }
      });
    }
  }, [page, router]);

  if (notFound) {
    // If isLoggedIn changes after a Page Not Found has been displayed, reloads
    // the page in case there's a Builder page targeted at the new login status.
    // This happens, for example, if a non-logged-in user visits a URL that only
    // exists for logged-in users, and the Page Not Found page is displayed,
    // then if the user signs in from the LOG IN link in the page, this reloads
    // the page to show the content for logged-in users.
    if (prevIsLoggedIn.current !== isLoggedIn) {
      window.location.reload();
      return null;
    }

    return (
      <ErrorTemplate>
        <ErrorPageLayout statusCode={404} message="Page Not Found" />
      </ErrorTemplate>
    );
  }

  return page ? <BuilderPage {...props} /> : null;
}

BuilderPageOrNavigate.getInitialProps = async ctx => {
  const pathname = ctx.asPath?.split('?') ? ctx.asPath?.split('?')[0] : null;
  await ctx.store.dispatch(loadSession());

  try {
    const isPreview = isBuilderPreview(ctx.req);
    const { builderJson, builderStateData } = await getBuilderJsonSSR({
      modelName: 'page',
      urlPath: pathname,
      ctx,
      isPreview,
    });

    return {
      page: builderJson,
      builderStateData,
      modelName: 'page',
      urlPath: pathname,
      isPreview,
    };
  } catch (error) {
    Sentry.captureException(error);

    return {
      error,
    };
  }
};

BuilderPageOrNavigate.propTypes = {
  builderStateData: PropTypes.object,
  error: PropTypes.object,
  isPreview: PropTypes.bool,
  modelName: PropTypes.string,
  urlPath: PropTypes.string,
  page: PropTypes.object,
};

registerBuilderComponents(nodeComponents);
registerBuilderNodesMenu();

export default createPage(BuilderPageOrNavigate, {
  // same resource bundles as products-bmig
  resourceBundles: [
    'inline_size_grid',
    'site_product_category',
    'site_product_filter',
    'site_product_grid',
    'site_product_option',
    'site_pdp',
    'site_home',
    'site_ccpa',
    'site_error',
  ],
  prefetchFeatures: ['match_user_size_with_model_size'],
});
